/** What K3's Moliya reads and writes beyond the finance endpoints every screen shares. */
import { apiRequest } from "@/lib/api";

import type { CashAccount, Currency, Expense, PaymentDraft, Payment, PaymentLink } from "./types";

/** JSON, unless a receipt is attached: then multipart, where `null` ("no instalment") is left out. */
export function paymentBody({ receipt, ...draft }: PaymentDraft): FormData | typeof draft {
  if (receipt === undefined) {
    return draft;
  }
  const body = new FormData();
  for (const [key, value] of Object.entries(draft) as [string, string | number | null | undefined][]) {
    if (value !== null && value !== undefined) {
      body.append(key, String(value));
    }
  }
  body.append("receipt", receipt);
  return body;
}

export interface CurrencyAmount {
  currency: Currency;
  amount: number;
}

export interface FinanceSummary {
  income_uzs: number;
  /** Null when a row behind today's income has no frozen dollar figure (§5). */
  income_usd: number | null;
  income_count: number;
  income_series: number[];
  debts: CurrencyAmount[];
  debt_deals: number;
  debt_overdue: number;
  debt_series: number[];
  provider_due: CurrencyAmount[];
  provider_series: number[];
  pending: number;
  pending_series: number[];
  payments_count: number;
  providers_count: number;
}

export interface ProviderPaymentRow {
  booking_id: number;
  provider: string;
  ref: string;
  deal_id: number;
  client_name: string;
  owed: number;
  cost_price: number;
  currency: Currency;
  client_paid: number;
  deal_currency: Currency;
  due: string;
  date_from: string | null;
  is_paid: boolean;
  is_acked: boolean;
  has_alert: boolean;
  operator_booking_id: number | null;
}

export interface KassaCard {
  account: CashAccount & { branch: number | null; branch_name: string | null };
  balance: number;
  today_movement: number;
  last_movement_on: string | null;
  last_in: number;
  last_out: number;
  overdrawn_by_provider: boolean;
}

export interface MoliyaDebtor {
  deal_id: number;
  client_name: string;
  destination: string;
  currency: Currency;
  price: number;
  paid: number;
  remaining: number;
  next_expected_date: string | null;
  date_from: string | null;
}

type Branch = number | null;
const branchQuery = (branch: Branch): { branch?: number } => (branch === null ? {} : { branch });

export const moliyaApi = {
  summary: (branch: Branch): Promise<FinanceSummary> =>
    apiRequest<FinanceSummary>("/api/finance/summary/", { query: branchQuery(branch) }),

  overview: (branch: Branch): Promise<{ accounts: KassaCard[] }> =>
    apiRequest<{ accounts: KassaCard[] }>("/api/finance/overview/", { query: branchQuery(branch) }),

  debtors: (branch: Branch): Promise<{ debtors: MoliyaDebtor[] }> =>
    apiRequest<{ debtors: MoliyaDebtor[] }>("/api/finance/reports/debtors/", { query: branchQuery(branch) }),

  providerPayments: (branch: Branch): Promise<ProviderPaymentRow[]> =>
    apiRequest<ProviderPaymentRow[]>("/api/finance/provider-payments/", { query: branchQuery(branch) }),

  payProvider: (
    bookingId: number,
    body: { cash_account: number; amount: string; date: string },
    idempotencyKey: string,
  ): Promise<Expense> =>
    apiRequest<Expense>(`/api/finance/provider-payments/${String(bookingId)}/pay/`, {
      method: "POST",
      body,
      idempotencyKey,
    }),

  ack: (bookingId: number): Promise<null> =>
    apiRequest<null>(`/api/finance/provider-payments/${String(bookingId)}/ack/`, { method: "POST" }),

  unack: (bookingId: number): Promise<null> =>
    apiRequest<null>(`/api/finance/provider-payments/${String(bookingId)}/ack/`, { method: "DELETE" }),

  sendLink: (dealId: number, idempotencyKey: string): Promise<{ link: PaymentLink; channel: string | null }> =>
    apiRequest<{ link: PaymentLink; channel: string | null }>(`/api/finance/deals/${String(dealId)}/send-link/`, {
      method: "POST",
      idempotencyKey,
    }),

  createPayment: (draft: PaymentDraft & { manual_rate?: string }, idempotencyKey: string): Promise<Payment> =>
    apiRequest<Payment>("/api/finance/payments/", { method: "POST", body: paymentBody(draft), idempotencyKey }),

  approve: (id: number, idempotencyKey: string): Promise<Payment> =>
    apiRequest<Payment>(`/api/finance/payments/${String(id)}/approve/`, { method: "POST", idempotencyKey }),
};
